import { SUBSCRIBE_FORM_ID } from "../../const";
import type { MarketDataPlans } from "../../util";
import { FixedBottomMenu } from "../FixedBottomMenu";
import type { PaymentSchedule } from "../types";
import { BenefitsCard } from "./presentation/BenefitsCard";
import { PaymentMethodToggle } from "./presentation/PaymentMethodToggle";
import { PaymentScheduleToggle } from "./presentation/PaymentScheduleToggle";
import { PlaidCard } from "./presentation/PlaidCard";
import { StripeCard } from "./presentation/StripeCard";
import { SuccessModal } from "./presentation/SuccessModal";
import { SummaryCard } from "./presentation/SummaryCard";
import type { CouponProps } from "./Review.connector";
import type { ReviewControllerReturn } from "./Review.controller";

interface ReviewPresProps
  extends Omit<ReviewControllerReturn, "stripe" | "elements"> {
  isElite: boolean;
  paymentSchedule: PaymentSchedule;
  onPaymentScheduleChange: (paymentSchedule: PaymentSchedule) => void;
  nextBillingDate: string;
  onSubscribeClicked: () => void;
  isSubscribeLoading: boolean;
  isSubscribeDisabled: boolean;
  totalPrice: string;
  onStripeCcChange: () => void;
  stripeCCError: string | null;
  onBackClicked: () => void;
  plans: MarketDataPlans;
  isSuccessModalOpen: boolean;
  onSuccessModalClose: () => void;
  coupon: CouponProps;
}

export const ReviewPres = ({
  isElite,
  plans,
  paymentSchedule,
  onPaymentScheduleChange,
  paymentMethod,
  onPaymentMethodChange,
  isBankAccountPaymentEnabled,
  coupon,
  plaidStripeLink,
  nextBillingDate,
  onSubscribeClicked,
  isSubscribeLoading,
  isSubscribeDisabled,
  totalPrice,
  onStripeCcChange,
  stripeCCError,
  onBackClicked,
  isSuccessModalOpen,
  onSuccessModalClose,
}: ReviewPresProps) => (
  <div className="flex max-w-[800px] flex-col gap-8">
    {!isElite && (
      <PaymentScheduleToggle
        paymentSchedule={paymentSchedule}
        onPaymentScheduleChange={onPaymentScheduleChange}
      />
    )}
    {/** Mobile columns, desktop grid layout */}
    <div className="flex w-full flex-col gap-8 sm:grid sm:grid-cols-[1fr_296px] sm:grid-rows-[auto_auto] sm:gap-x-6 sm:gap-y-8">
      <BenefitsCard
        className="sm:col-start-1 sm:col-end-2 sm:row-start-1 sm:row-end-2 sm:min-w-0"
        plans={plans}
        isElite={isElite}
        paymentSchedule={paymentSchedule}
      />
      {!isElite && (
        <section className="flex flex-col gap-4 sm:col-start-1 sm:col-end-2 sm:row-start-2 sm:row-end-3 sm:min-w-0">
          <h2 className="m-0 p-0 text-left font-semibold text-gray-800 text-lg">
            Select Payment Method
          </h2>
          <PaymentMethodToggle
            paymentMethod={paymentMethod}
            onPaymentMethodChange={onPaymentMethodChange}
            isBankAccountEnabled={isBankAccountPaymentEnabled}
          />
          {paymentMethod === "stripe" && (
            <StripeCard
              onChange={onStripeCcChange}
              errorMessage={stripeCCError}
            />
          )}
          {paymentMethod === "plaid" && (
            <PlaidCard
              plaidAccount={plaidStripeLink.account}
              onLinkBankAccount={plaidStripeLink.onOpen}
              isLinkButtonDisabled={!plaidStripeLink.isReady}
              isLinkButtonLoading={plaidStripeLink.isLoading}
            />
          )}
        </section>
      )}
      <SummaryCard
        className="sm:col-start-2 sm:col-end-3 sm:row-start-1 sm:row-end-3 sm:min-w-0"
        isElite={isElite}
        paymentSchedule={paymentSchedule}
        couponCode={coupon.code}
        couponCodeError={coupon.error}
        onCouponCodeChange={coupon.onChange}
        appliedCoupon={coupon.appliedCoupon}
        onApplyCoupon={coupon.onApply}
        isApplyCouponLoading={coupon.isLoading}
        isApplyCouponDisabled={coupon.isDisabled}
        nextBillingDate={nextBillingDate}
        totalPrice={totalPrice}
        discount={coupon.discount}
        plans={plans}
        onRemoveCoupon={coupon.onRemove}
      />
    </div>

    {isSuccessModalOpen && (
      <SuccessModal isOpen={isSuccessModalOpen} onClose={onSuccessModalClose} />
    )}

    <FixedBottomMenu
      back={{
        onClick: onBackClicked,
        disabled: isSubscribeLoading,
      }}
      next={{
        label: "Subscribe",
        onClick: onSubscribeClicked,
        disabled: isSubscribeDisabled,
        loading: isSubscribeLoading,
        // @ts-expect-error
        formId: SUBSCRIBE_FORM_ID,
        type: "submit",
      }}
    />
  </div>
);
